import {
  CloseButton,
  Flex,
  Select,
  SimpleGrid,
  TextInput,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { DataTableSortStatus } from "mantine-datatable";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";

import { useLimitRequests } from "../../../api/hooks/limitRequest/useLimitRequests.ts";
import SectionTitle from "../../../components/Section/SectionTitle/SectionTitle.tsx";
import Table from "../../../components/Table/Table.tsx";
import { SortDirection, TableRow } from "../../../components/Table/types.ts";
import { AppRoute } from "../../../constants.ts";
import {
  LimitRequestFilter,
  LimitRequestOrderBy,
  LimitRequestStatusFilterOptions,
  OrderBy,
  StringFilterMode,
} from "../../../graphql/generated.ts";
import { CustomerOption } from "../../../types/customer.ts";
import {
  LimitRequest,
  limitRequestTypeOptions,
} from "../../../types/limitRequest/limitRequest.ts";
import { getCompanyOptions } from "../../../utils/company.ts";
import { getLimitRequestApiFilter } from "../../../utils/filter.ts";
import { getPagesCount } from "../../../utils/pagination.ts";
import { getRiskMonitoringApiSort } from "../utils.ts";
import { useOngoingOLTableData } from "./hooks/useOngoingOLTableData.tsx";

interface Props {
  companies: CustomerOption[] | undefined;
}

const OngoingOLTable = ({ companies }: Props) => {
  const navigate = useNavigate();

  const [filter, setFilter] = useState<LimitRequestFilter>({});
  const [search, setSearch] = useState<string | undefined>();
  const [debouncedSearch] = useDebouncedValue(search, 300);
  const [orderBy, setOrderBy] = useState<LimitRequestOrderBy>({
    createdAt: OrderBy.Desc,
  });
  const [page, setPage] = useState<number>(1);

  const [{ data, fetching, error }] = useLimitRequests({
    filter: {
      ...filter,
      requestStatus: {
        in: [
          LimitRequestStatusFilterOptions.Ol,
          LimitRequestStatusFilterOptions.OlWaitingForCustomer,
        ],
      },
      limitName: debouncedSearch
        ? {
            contains: debouncedSearch,
            mode: StringFilterMode.Insensitive,
          }
        : undefined,
    },
    orderBy,
    page,
  });

  const ongoingOLData: LimitRequest[] | null | undefined =
    data?.limitRequests?.data;

  const pageCount = getPagesCount(data?.limitRequests?.total);

  const handlePageChange = useCallback(
    (page: number) => {
      setPage(page);
    },
    [setPage]
  );

  const handleRowClik = useCallback(
    (record: TableRow) => {
      navigate(`${AppRoute.LimitRequest}/${record.id}`);
    },
    [navigate]
  );

  const handleSortChange = useCallback(
    (sort: DataTableSortStatus) => {
      setOrderBy(getRiskMonitoringApiSort(sort));
    },
    [setOrderBy]
  );

  const handleFilterChange = useCallback((key: string, value: unknown) => {
    setFilter(getLimitRequestApiFilter({ [key]: value }));
  }, []);

  const [columns, rows] = useOngoingOLTableData({
    data: ongoingOLData,
  });

  if (error) {
    notifications.clean();
    notifications.show({
      message: "Something went wrong while trying to fetch Ongoing OL data.",
      variant: "error",
    });
  }

  return (
    <Flex direction="column" gap={12}>
      <SectionTitle>Ongoing OL</SectionTitle>
      <SimpleGrid
        cols={3}
        breakpoints={[{ maxWidth: "sm", cols: 2 }]}
        spacing={20}
        verticalSpacing={12}
        pb={8}
      >
        <Select
          size="m"
          clearable
          searchable
          label="Company name"
          placeholder="Select Company name"
          data={getCompanyOptions(companies)}
          onChange={(value) => handleFilterChange("companyName", value)}
        />
        <Select
          size="m"
          clearable
          label="Limit Type"
          placeholder="Select Limit Type"
          data={limitRequestTypeOptions}
          onChange={(value) => handleFilterChange("limitRequestType", value)}
        />
        <TextInput
          size="m"
          label="Limit Name"
          placeholder="Enter Limit Name"
          rightSection={
            <CloseButton
              aria-label="Clear input"
              variant="transparent"
              size="md"
              mr="5px"
              style={{
                display: search ? undefined : "none",
              }}
              onClick={() => setSearch("")}
            />
          }
          value={search}
          onChange={(e) => setSearch(e.currentTarget.value)}
        />
      </SimpleGrid>
      <Table
        columns={columns}
        rows={rows}
        defaultSort={{
          columnAccessor: "createdAt",
          direction: SortDirection.asc,
        }}
        loading={fetching}
        pagination={{
          pageCount: pageCount,
          page: page,
          onPageChange: handlePageChange,
        }}
        onRowClick={handleRowClik}
        onSortChange={handleSortChange}
      />
    </Flex>
  );
};

export default OngoingOLTable;
